import { useState, useMemo, useRef } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
  } from '@mui/material';
  import { useDateSort } from '../../../hooks/useDateSort';
  import TableActionsMenu from '../../../components/common/TableActionsMenu';
  import { AdcashBuzzvilMediationMetrics } from '../../../types/adcash.types';

  type Props = {
    data: AdcashBuzzvilMediationMetrics;
  };

  export default function AdcashBuzzvilMediationMetricsTable({ data }: Props) {
    const { mediationList, mediationStats } = data;
    const rowsPerPage = 31;
    const [page, setPage] = useState(0);
    
    // 테이블 ref - 엑셀 다운로드용
    const tableRef = useRef<HTMLTableElement>(null);

    // mediationStats를 날짜별로 그룹화하고 각 미디에이션별 revenue 매핑
    const tableData = useMemo(() => {
      // 날짜별로 그룹화
      const dateMap = new Map<string, Record<string, number>>();
      
      mediationStats.forEach((stat) => {
        const { date, mediation, revenue } = stat;
        if (!dateMap.has(date)) {
          dateMap.set(date, {});
        }
        const dateData = dateMap.get(date);
        if (dateData) {
          dateData[mediation] = revenue;
        }
      });
      
      // 날짜별로 배열 생성
      return Array.from(dateMap.entries())
        .map(([date, mediationRevenues]) => ({
          date,
          mediationRevenues
        }));
    }, [mediationStats]);

    const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(tableData);

    const paged = useMemo(
        () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedData, page]
    );

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);
    };

    // 각 미디에이션별 전체 합계 계산
    const totals = useMemo(() => {
        const totalsMap: Record<string, number> = {};
        
        mediationList.forEach((mediation) => {
            totalsMap[mediation] = sortedData.reduce((sum, row) => {
                return sum + (row.mediationRevenues[mediation] || 0);
            }, 0);
        });

        return totalsMap;
    }, [sortedData, mediationList]);

    // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시, 합계 행 제외)
    const formatNumberOnly = (value: number) => Math.round(value).toLocaleString('ko-KR');
    const allRowsDataForExcel = useMemo(() => {
        return sortedData.map((row) => {
            return [
                row.date,
                ...mediationList.map((mediation) => {
                    const revenue = row.mediationRevenues[mediation] || 0;
                    return formatNumberOnly(revenue);
                })
            ];
        });
    }, [sortedData, mediationList]);

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mt: 2,
                    ml: 2
                }}
            >
                <TableActionsMenu tableRef={tableRef} allRowsData={allRowsDataForExcel} />
            </Box>
            <TableContainer
                sx={{
                    overflowX: 'auto',
                    mt: 2
                }}
            >
              <Table ref={tableRef}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active
                        direction={sortDirection}
                        onClick={toggleSort}
                      >
                        날짜
                      </TableSortLabel>
                    </TableCell>
                    {mediationList.map((mediation) => (
                      <TableCell key={mediation} align="right" sx={{ fontWeight: 'bold' }}>
                        {mediation}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                    {/* 합계 행 */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
                        {mediationList.map((mediation) => (
                            <TableCell key={mediation} align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(totals[mediation])}
                            </TableCell>
                        ))}
                    </TableRow>
                    {paged.map((row) => {
                        return (
                            <TableRow key={row.date}>
                                <TableCell>{row.date}</TableCell>
                                {mediationList.map((mediation) => {
                                  const revenue = row.mediationRevenues[mediation] || 0;
                                  return (
                                    <TableCell key={mediation} align="right">
                                      {formatCurrency(revenue)}
                                    </TableCell>
                                  );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={sortedData.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
            />
        </Box>
    );
  }
  